// The rack's voice: his jam, played by the world.
//
// The sim writes a jam as a Strudel pattern and times it with Strudel's
// own maths into a list of events (world/Tunes.js): a frequency, when,
// for how long, and the sound each note asked for (a wave shape, a
// filter cutoff, an envelope, a gain). Those are exactly the parts of a
// small modular patch, so this plays them as one: oscillator into a
// lowpass into a gain envelope, one voice per note, through the same
// audio context and the same distance mixing the piano uses. No samples
// and no library in the browser. The rack in the world and the play
// button on his page are the same synth in two places, so what a visitor
// hears standing by the rack is what they hear on the page.
//
// The drums (20 Oct, the rack's drum module): an event with `drum` is a
// hit, not a note. A kick is a sine that drops from 150 to 50 Hz; a snare
// is a burst of noise through a bandpass with a short sine under it; a hat
// is noise through a highpass, 40 ms. Synthesised here, no samples, through
// the same echo and room sends. The jams page carries the same three
// (world/Tunes.js PLAYER); keep them in step.
//
// The drum machine beside the rack (24 Oct, sim-server world/Drums.js)
// plays through here too, and adds a tom: a sine that falls from 220 to
// 110 Hz over 180 ms. His music page carries it (world/Music.js PLAYER).
//
// The jam is looped for as long as he sits. Stopping matters as much as
// starting: a rack still droning after he walks off would undo the whole
// trick, same as the piano.

import { getCtx, audioReady } from './context'

let playing = null // { master, filter, timer, jamId, fx }

// The effects module (sim world/Tunes.js, RACK_MODULES fx): an echo and
// a room. One delay line and one convolver per jam, shared by every
// voice; each note sends as much of itself into them as its pattern
// asked for (.delay and .room, nought to one), so the same jam played
// on his page and by the rack sounds the same.
function makeRoom(ctx) {
  const seconds = 1.8
  const rate = ctx.sampleRate
  const buf = ctx.createBuffer(2, Math.floor(rate * seconds), rate)
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch)
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 2.6)
  }
  const conv = ctx.createConvolver()
  conv.buffer = buf
  return conv
}
function makeEcho(ctx) {
  const delay = ctx.createDelay(1.5)
  delay.delayTime.value = 0.375
  const feedback = ctx.createGain()
  feedback.gain.value = 0.35
  const tone = ctx.createBiquadFilter()
  tone.type = 'lowpass'
  tone.frequency.value = 3200
  delay.connect(tone).connect(feedback).connect(delay)
  return delay
}

// A second of white noise, made once per context: the snare and the hat
// are shaped out of it.
let noiseFor = null
function noiseBuffer(ctx) {
  if (noiseFor?.ctx === ctx) return noiseFor.buf
  const buf = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate)
  const d = buf.getChannelData(0)
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1
  noiseFor = { ctx, buf }
  return buf
}

// Sends into the effects, as much as the event asked for.
function sendTo(ctx, env, e, fx) {
  const echo = Math.max(0, Math.min(1, Number(e.delay) || 0))
  if (fx?.echo && echo > 0) {
    const send = ctx.createGain()
    send.gain.value = echo * 0.8
    env.connect(send).connect(fx.echo)
  }
  const room = Math.max(0, Math.min(1, Number(e.room) || 0))
  if (fx?.room && room > 0) {
    const send = ctx.createGain()
    send.gain.value = room * 0.6
    env.connect(send).connect(fx.room)
  }
}

export const DRUMS = ['bd', 'sd', 'hh', 'lt']

function drumVoice(ctx, dest, e, when, fx = null) {
  const level = Math.max(0.01, Math.min(1, Number(e.gain) || 0.5)) * 0.35
  const env = ctx.createGain()
  env.connect(dest)
  if (e.drum === 'bd') {
    const osc = ctx.createOscillator()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(150, when)
    osc.frequency.exponentialRampToValueAtTime(50, when + 0.08)
    env.gain.setValueAtTime(level, when)
    env.gain.exponentialRampToValueAtTime(0.0001, when + 0.25)
    osc.connect(env)
    osc.start(when)
    osc.stop(when + 0.3)
  } else if (e.drum === 'lt') {
    const osc = ctx.createOscillator()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(220, when)
    osc.frequency.exponentialRampToValueAtTime(110, when + 0.18)
    env.gain.setValueAtTime(level, when)
    env.gain.exponentialRampToValueAtTime(0.0001, when + 0.35)
    osc.connect(env)
    osc.start(when)
    osc.stop(when + 0.4)
  } else {
    const src = ctx.createBufferSource()
    src.buffer = noiseBuffer(ctx)
    const f = ctx.createBiquadFilter()
    if (e.drum === 'sd') {
      f.type = 'bandpass'
      f.frequency.value = 1800
      f.Q.value = 1
      env.gain.setValueAtTime(level, when)
      env.gain.exponentialRampToValueAtTime(0.0001, when + 0.15)
      // The body of the snare: a short sine under the noise.
      const body = ctx.createOscillator()
      body.type = 'sine'
      body.frequency.value = 180
      const bodyEnv = ctx.createGain()
      bodyEnv.gain.setValueAtTime(level * 0.6, when)
      bodyEnv.gain.exponentialRampToValueAtTime(0.0001, when + 0.1)
      body.connect(bodyEnv).connect(dest)
      body.start(when)
      body.stop(when + 0.12)
    } else {
      f.type = 'highpass'
      f.frequency.value = 7000
      env.gain.setValueAtTime(level * 0.7, when)
      env.gain.exponentialRampToValueAtTime(0.0001, when + 0.04)
    }
    src.connect(f).connect(env)
    src.start(when)
    src.stop(when + 0.3)
  }
  sendTo(ctx, env, e, fx)
}

function voice(ctx, dest, e, when, fx = null) {
  if (e.drum) return drumVoice(ctx, dest, e, when, fx)
  const osc = ctx.createOscillator()
  osc.type = ['sawtooth', 'square', 'triangle', 'sine'].includes(e.wave) ? e.wave : 'triangle'
  osc.frequency.value = e.freq
  const lp = ctx.createBiquadFilter()
  lp.type = 'lowpass'
  lp.frequency.value = Math.max(80, Math.min(16000, Number(e.cutoff) || 2400))
  lp.Q.value = 0.9
  const env = ctx.createGain()
  const attack = Math.max(0.005, Number(e.attack) || 0.01)
  const release = Math.max(0.02, Number(e.release) || 0.3)
  const level = Math.max(0.01, Math.min(1, Number(e.gain) || 0.6)) * 0.35
  const end = when + Math.max(0.05, Number(e.dur) || 0.25)
  env.gain.setValueAtTime(0.0001, when)
  env.gain.exponentialRampToValueAtTime(level, when + attack)
  env.gain.setValueAtTime(level, Math.max(when + attack, end - 0.01))
  env.gain.exponentialRampToValueAtTime(0.0001, end + release)
  osc.connect(lp).connect(env).connect(dest)
  sendTo(ctx, env, e, fx)
  osc.start(when)
  osc.stop(end + release + 0.05)
}

/**
 * Start the rack on a jam. Idempotent per jam id: a second call for the
 * same jam is a no-op, a different jam replaces the last. `jam` is what
 * the sim puts on the rack entity: { id, seconds, events }.
 */
export function startRack(jam) {
  if (!jam?.events?.length || !audioReady()) return
  if (playing?.jamId === jam.id) return
  stopRack()
  const ctx = getCtx()
  const master = ctx.createGain()
  master.gain.value = 0
  const filter = ctx.createBiquadFilter()
  filter.type = 'lowpass'
  filter.frequency.value = 16000
  master.connect(filter).connect(ctx.destination)
  // The effects only exist when a note asks for them.
  const fx = { echo: null, room: null }
  if (jam.events.some((e) => Number(e.delay) > 0)) { fx.echo = makeEcho(ctx); fx.echo.connect(master) }
  if (jam.events.some((e) => Number(e.room) > 0)) { try { fx.room = makeRoom(ctx); fx.room.connect(master) } catch { fx.room = null } }

  const seconds = Math.max(1, Number(jam.seconds) || 16)
  let cycleStart = ctx.currentTime + 0.05
  const schedule = () => {
    for (const e of jam.events) voice(ctx, master, e, cycleStart + (Number(e.at) || 0), fx)
    cycleStart += seconds
  }
  // Two passes ahead, then one per loop, so the schedule never runs dry
  // and never stacks up.
  schedule(); schedule()
  const timer = setInterval(() => {
    if (!playing) return
    if (cycleStart - ctx.currentTime < seconds * 1.5) schedule()
  }, 500)
  playing = { master, filter, timer, jamId: jam.id, fx }
}

export function stopRack() {
  if (!playing) return
  const { master, filter, timer, fx } = playing
  clearInterval(timer)
  try {
    const ctx = getCtx()
    master.gain.setTargetAtTime(0, ctx.currentTime, 0.08)
    setTimeout(() => {
      try { master.disconnect(); filter.disconnect() } catch { /* gone */ }
      try { fx?.echo?.disconnect(); fx?.room?.disconnect() } catch { /* gone */ }
    }, 500)
  } catch { /* context gone */ }
  playing = null
}

export function isRackPlaying() { return !!playing }

/**
 * Distance mixing, same law as the piano: gain falls with the square of
 * distance and a lowpass closes in, so far away the rack is a hum on the
 * air and up close it is a bird with a patch.
 */
export function setRackDistance(d) {
  if (!playing) return
  const ctx = getCtx()
  const near = 3
  const gain = Math.min(1, (near * near) / Math.max(near * near, d * d)) * 0.9
  const cutoff = 400 + 15600 * Math.min(1, near / Math.max(near, d))
  playing.master.gain.setTargetAtTime(gain, ctx.currentTime, 0.1)
  playing.filter.frequency.setTargetAtTime(cutoff, ctx.currentTime, 0.1)
}
